/* eslint-disable */
/**
 * BestWorstCard — Best trade, Worst trade, Win/Loss ratio
 *
 * Props:
 *   best, worst   : trade entry objects { date, dollars, shares, buyPrice, pnl, pnlPct }
 *   winCount      : number of profitable trades
 *   lossCount     : number of losing trades
 *   totalCount    : total trades with P&L
 *
 * Usage:
 *   import BestWorstCard from "./components/charts/BestWorstCard.jsx";
 *   <BestWorstCard best={bestEntry} worst={worstEntry}
 *     winCount={winCount} lossCount={lossCount} totalCount={entriesWithPnl.length} />
 */
import { C, fmt$, fmtPct, formatDate } from "../shared/globals.js";

const FM = "Montserrat,sans-serif";

function StatRow({ label, value, valueColor }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
      padding:"7px 0", borderBottom:"1px solid var(--border)" }}>
      <span style={{ fontFamily:FM, fontSize:9, fontWeight:700,
        letterSpacing:"0.15em", color:"var(--text4)" }}>
        {label}
      </span>
      <span style={{ fontFamily:FM, fontSize:12, fontWeight:700,
        color: valueColor || "var(--text)" }}>
        {value}
      </span>
    </div>
  );
}

function TradeCard({ label, entry, color }) {
  const fmtD = s => { const [y,m,d] = s.split("-"); return `${d}.${m}.${y}`; };
  return (
    <div style={{
      flex:1, minWidth:180,
      background:"var(--bg2)",
      border:`1px solid var(--border)`,
      borderRadius:10,
      padding:"14px 16px",
    }}>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div style={{ fontFamily:FM, fontSize:8, fontWeight:700,
          letterSpacing:"0.25em", color:"var(--text4)" }}>
          {label}
        </div>
        <div style={{ fontFamily:FM, fontSize:8, fontWeight:700,
          color:"var(--text4)" }}>
          {fmtD(entry.date)}
        </div>
      </div>

      {/* Big P&L */}
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:14 }}>
        <i className={color === C.green ? "ph ph-trend-up" : "ph ph-trend-down"}
          style={{ fontSize:15, color, flexShrink:0 }}/>
        <span style={{ fontFamily:FM, fontSize:26, fontWeight:900,
          color, letterSpacing:"-0.02em", lineHeight:1 }}>
          {fmtPct(entry.pnlPct)}
        </span>
      </div>

      {/* Stats */}
      <div>
        <StatRow label="INVESTED"  value={fmt$(entry.dollars)}  />
        <StatRow label="BUY PRICE" value={fmt$(entry.buyPrice)} />
        <StatRow label="P&L"       value={fmt$(entry.pnl)} valueColor={color} />
      </div>
    </div>
  );
}

function WinLossCard({ winCount, lossCount, totalCount }) {
  const winPct  = totalCount > 0 ? (winCount  / totalCount) * 100 : 0;
  const lossPct = totalCount > 0 ? (lossCount / totalCount) * 100 : 0;

  return (
    <div style={{
      flex:1, minWidth:180,
      background:"var(--bg2)",
      border:"1px solid var(--border)",
      borderRadius:10,
      padding:"14px 16px",
    }}>
      {/* Header */}
      <div style={{ fontFamily:FM, fontSize:8, fontWeight:700,
        letterSpacing:"0.25em", color:"var(--text4)", marginBottom:12 }}>
        WIN / LOSS
      </div>

      {/* Counts */}
      <div style={{ display:"flex", alignItems:"flex-end", gap:0, marginBottom:14 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontFamily:FM, fontSize:8, fontWeight:700,
            letterSpacing:"0.15em", color:"var(--text4)", marginBottom:4 }}>IN PROFIT</div>
          <div style={{ fontFamily:FM, fontSize:26, fontWeight:900,
            color:C.green, letterSpacing:"-0.02em", lineHeight:1 }}>
            {winCount}
          </div>
          <div style={{ fontFamily:FM, fontSize:11, fontWeight:700,
            color:C.green, marginTop:4 }}>
            {winPct.toFixed(1)}%
          </div>
        </div>

        <div style={{ width:1, background:"var(--border)", alignSelf:"stretch", margin:"0 14px" }}/>

        <div style={{ flex:1, textAlign:"right" }}>
          <div style={{ fontFamily:FM, fontSize:8, fontWeight:700,
            letterSpacing:"0.15em", color:"var(--text4)", marginBottom:4 }}>IN LOSS</div>
          <div style={{ fontFamily:FM, fontSize:26, fontWeight:900,
            color:C.red, letterSpacing:"-0.02em", lineHeight:1 }}>
            {lossCount}
          </div>
          <div style={{ fontFamily:FM, fontSize:11, fontWeight:700,
            color:C.red, marginTop:4 }}>
            {lossPct.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Stats */}
      <div>
        <StatRow label="TOTAL TRADES" value={totalCount} />
        <StatRow
          label="WIN RATE"
          value={winPct.toFixed(1) + "%"}
          valueColor={winPct >= 50 ? C.green : C.red}
        />
      </div>

      {/* Progress bar */}
      <div style={{ height:3, borderRadius:2, background:"var(--bg3)",
        overflow:"hidden", marginTop:14 }}>
        <div style={{ height:"100%", width:`${winPct}%`,
          background:C.green, borderRadius:2,
          transition:"width 0.6s ease" }}/>
      </div>
    </div>
  );
}

export default function BestWorstCard({ best, worst, winCount, lossCount, totalCount }) {
  if (!best || !worst) return null;
  return (
    <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
      <TradeCard label="BEST TRADE"  entry={best}  color={C.green} />
      <TradeCard label="WORST TRADE" entry={worst} color={C.red}   />
      <WinLossCard winCount={winCount} lossCount={lossCount} totalCount={totalCount} />
    </div>
  );
}
